import { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiErrorResponse, createApiError } from '@/domain/errors/api.error';
import { toast } from 'sonner';

/**
 * Extend Axios Request Config to include our custom error handling options.
 */
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
    disableGlobalError?: boolean;
    onCustomError?: (error: ApiErrorResponse) => void;
}

/**
 * Error Interceptor: Converts Axios errors into a domain-level ApiError model.
 * Also handles global toast notifications based on status codes and overrides.
 */
export const onError = (error: AxiosError): never => {
    const config = error.config as ExtendedAxiosRequestConfig;
    let apiError: ApiErrorResponse;

    if (error.response) {
        // 1. Server responded with a status code outside the 2xx range
        const { status, data } = error.response;
        const body = data as any;

        apiError = createApiError({
            statusCode: status,
            message: body?.message || error.message || 'An unexpected error occurred',
            code: body?.code || 'SERVER_ERROR',
            details: body?.errors || body,
        });
    } else if (error.request) {
        // 2. Request was made but no response was received (Network error / Timeout)
        apiError = createApiError({
            statusCode: 0,
            message: 'Network error: No response from server. Please check your connection.',
            code: 'NETWORK_ERROR',
        });
    } else {
        // 3. Something happened in setting up the request
        apiError = createApiError({
            statusCode: -1,
            message: error.message || 'Failed to initialize request',
            code: 'UNKNOWN_ERROR',
        });
    }

    // Handle global error logic
    if (config?.onCustomError) {
        config.onCustomError(apiError);
    } else if (!config?.disableGlobalError) {
        showGlobalToast(apiError);
    }

    throw apiError;
};

/**
 * Helper to show global toast based on status code.
 */
const showGlobalToast = (error: ApiErrorResponse) => {
    const { statusCode, message } = error;

    // Only show toast for specific error ranges or common status codes
    const statusMessages: Record<number, string> = {
        400: 'Bad Request: Please check your input.',
        401: 'Unauthorized: Please login again.',
        403: 'Forbidden: You do not have permission.',
        404: 'Resource not found.',
        500: 'Internal server error. Please try again later.',
    };

    const title = statusMessages[statusCode] || 'An error occurred';

    // Using sonner for toasts
    toast.error(title, {
        description: message,
    });
};
